'use client';

import { useEffect, useState, useRef } from 'react';
import DOMPurify from 'dompurify';
import { IconChevronDown } from '@repo/svgs';
import { cn } from '@/lib/cn';

interface DescriptionProps {
  description: string;
  customTitle?: string;
  customTitleStyle?: string;
  className?: string;
  maxHeight?: string;
}

export function Description({
  description,
  customTitle,
  customTitleStyle = '',
  className,
  maxHeight = 'max-h-[185px]',
}: DescriptionProps) {
  const [expanded, setExpanded] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [sanitizedHtml, setSanitizedHtml] = useState<string>('');
  const descriptionRef = useRef<HTMLDivElement>(null);

  // Sanitize HTML content ONLY on client-side to prevent saxes file system errors during SSR
  useEffect(() => {
    if (!description) {
      setSanitizedHtml('');
      return;
    }

    // Clean leading and trailing whitespace
    const trimmedDescription = description.trim();

    if (!trimmedDescription) {
      setSanitizedHtml('');
      return;
    }

    // DOMPurify uses saxes internally, which tries to access files during SSR
    // Run sanitization only on client to avoid "no such file or directory" errors
    try {
      const sanitized = DOMPurify.sanitize(trimmedDescription);
      setSanitizedHtml(sanitized);
    } catch (error) {
      // If sanitization fails, fall back to plain text with escaped HTML
      console.warn('DOMPurify sanitization failed, using plain text:', error);
      setSanitizedHtml(trimmedDescription.replace(/</g, '&lt;').replace(/>/g, '&gt;'));
    }
  }, [description]);

  // Detect overflow using ResizeObserver instead of setTimeout + layout reads.
  // ResizeObserver callbacks run after the browser has already computed layout,
  // so reading scrollHeight/clientHeight here is "free" — no forced reflow.
  useEffect(() => {
    const el = descriptionRef.current;
    if (!el || !sanitizedHtml || expanded) return;

    const observer = new ResizeObserver(() => {
      if (!descriptionRef.current) return;
      const { scrollHeight, clientHeight } = descriptionRef.current;
      setShowMore(scrollHeight > clientHeight);
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, [sanitizedHtml, expanded]);

  if (!description) {
    return null;
  }

  return (
    <div className={className}>
      {customTitle && (
        <h2 className={cn('pb-4 text-xl font-semibold', customTitleStyle)}>{customTitle}</h2>
      )}
      <div>
        <div
          ref={descriptionRef}
          id="desc"
          className={cn(
            'whitespace-pre-line text-body-2 text-primary',
            !expanded && `${maxHeight} overflow-hidden line-clamp-5`,
          )}
          dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
        />
        {showMore && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 mt-2 text-body-2-high text-accent hover:underline"
          >
            <span>{expanded ? 'View less' : 'View more'}</span>
            <IconChevronDown
              width={16}
              height={16}
              className={cn('transition-transform', expanded && 'rotate-180')}
              style={{ color: 'var(--icon-color-accent)' }}
            />
          </button>
        )}
      </div>
    </div>
  );
}
